import { paletteData } from "./palette-data.js";

/**
 * A palette is three or four colours in no particular order.
 *
 * The lists run both ways — some open dark and end light, some the reverse —
 * so nothing here trusts the order they were written in. Every role a backdrop
 * needs is read off the colours themselves, which is what lets a palette be
 * pasted in as four hexes and still draw the right way round.
 */
export interface Palette {
  id: number;
  family: PaletteFamily;
  colours: readonly string[];
}

/**
 * How the list is browsed.
 *
 * There are 186 of them, which as a flat list is about five thousand pixels of
 * scroll — a list nobody reaches the end of. The families are computed rather
 * than typed in, so the next hundred file themselves.
 */
export const paletteFamilies = ["Blush", "Red", "Warm", "Green", "Teal", "Blue", "Purple", "Neutral"] as const;

export type PaletteFamily = (typeof paletteFamilies)[number];

export const palettes: readonly Palette[] = paletteData;

const channels = (colour: string): [number, number, number] => {
  const value = parseInt(colour.slice(1), 16);
  return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255];
};

export function paletteLuminance(colour: string): number {
  const [r, g, b] = channels(colour);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * How far from grey a colour is.
 *
 * Chroma rather than HSL saturation, which divides by lightness and so scores
 * a pale wash above a deep purple — picking the wash as the colour a palette
 * is "about" and drawing a gradient that looks nothing like the strip.
 */
export function paletteChroma(colour: string): number {
  const [r, g, b] = channels(colour);
  return Math.max(r, g, b) - Math.min(r, g, b);
}

/** The colour a palette is actually for: the one furthest from grey. */
export function liveliest(colours: readonly string[]): string {
  return [...colours].sort((a, b) => paletteChroma(b) - paletteChroma(a))[0];
}

/** Below this a palette has no colour worth leading with, and reads as neutral. */
export const NEUTRAL_CHROMA = 0.18;

/** Above this a red is a blush rather than a red. The one judgement call here. */
const BLUSH_LIGHTNESS = 0.55;

function hue(colour: string): number {
  const [r, g, b] = channels(colour);
  const max = Math.max(r, g, b);
  const span = max - Math.min(r, g, b);
  if (span === 0) return 0;
  const raw = max === r ? ((g - b) / span) % 6 : max === g ? (b - r) / span + 2 : (r - g) / span + 4;
  return (raw * 60 + 360) % 360;
}

export function paletteFamily(colours: readonly string[]): PaletteFamily {
  const lead = liveliest(colours);
  if (paletteChroma(lead) < NEUTRAL_CHROMA) return "Neutral";

  const angle = hue(lead);
  // Blush and Red are the same band of hues cut by lightness: without the cut
  // two thirds of the list files as "Red" and the filter stops filtering.
  if (angle < 20 || angle >= 330) return paletteLuminance(lead) > BLUSH_LIGHTNESS ? "Blush" : "Red";
  if (angle < 70) return "Warm";
  if (angle < 160) return "Green";
  if (angle < 200) return "Teal";
  if (angle < 255) return "Blue";
  return "Purple";
}

/**
 * Below this a sampled pixel is interface chrome — white, grey, near-black —
 * rather than colour. Screen recordings are mostly chrome, so this is what
 * separates "the app is blue" from "the app has a lot of white in it".
 */
const SAMPLE_COLOURED = 0.14;

/**
 * The share of coloured pixels below which a recording has no colour to speak
 * of. A monochrome terminal or a plain document should get a neutral backdrop,
 * not whatever hue happened to survive the threshold.
 */
const SAMPLE_ENOUGH = 0.03;

/**
 * The palette that answers a recording's own colours.
 *
 * Matched on hue rather than on the most common colour, because the most common
 * colour in a screen recording is white and a white backdrop behind a white app
 * is not a backdrop. What identifies a recording is its accent — the blue of
 * one product, the green of another — so the greys are dropped and the survivors
 * are averaged as angles.
 *
 * Averaged on the circle, weighted by chroma: hue is periodic, so a plain mean
 * of 350° and 10° is 180°, which is the exact opposite of both. Weighting by
 * chroma lets a small area of strong accent outvote a large wash of nearly-grey.
 */
export function paletteForColours(samples: readonly string[]): Palette {
  const coloured = samples.filter((colour) => paletteChroma(colour) >= SAMPLE_COLOURED);
  if (samples.length === 0 || coloured.length < samples.length * SAMPLE_ENOUGH) {
    return palettes.find((palette) => palette.family === "Neutral") ?? palettes[0];
  }

  let x = 0;
  let y = 0;
  for (const colour of coloured) {
    const weight = paletteChroma(colour);
    const angle = (hue(colour) * Math.PI) / 180;
    x += Math.cos(angle) * weight;
    y += Math.sin(angle) * weight;
  }
  const mean = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;

  const distance = (palette: Palette): number => {
    const lead = liveliest(palette.colours);
    // A neutral palette has no hue to compare, and reading one off near-grey
    // gives a number that means nothing but sorts anyway.
    if (paletteChroma(lead) < NEUTRAL_CHROMA) return Number.POSITIVE_INFINITY;
    const gap = Math.abs(hue(lead) - mean);
    return Math.min(gap, 360 - gap);
  };

  // Sorted rather than reduced so the id breaks ties: two palettes the same
  // distance from the same hue must not depend on array order to choose.
  return [...palettes].sort((a, b) => distance(a) - distance(b) || a.id - b.id)[0];
}

export interface PaletteRoles {
  /** What the backdrop sits on. */
  ground: string;
  /** Everything else, furthest from the ground first. */
  fields: readonly string[];
}

/**
 * Which colour does what, read off lightness rather than position.
 *
 * The ground is an end, never a middle: a palette whose middle leans dark gets
 * its darkest as the ground, one that leans light gets its lightest. Ordering
 * the fields by distance from the ground is what makes `fields[0]` mean "the
 * strong contrast" whichever way the palette runs — without it a gradient off
 * a dark palette is two shades of the same dark.
 */
export function paletteRoles(palette: Palette): PaletteRoles {
  const sorted = [...palette.colours].sort((a, b) => paletteLuminance(a) - paletteLuminance(b));
  const middle = sorted.slice(1, -1);
  const leansDark = middle.reduce((sum, colour) => sum + paletteLuminance(colour), 0) / middle.length < 0.5;

  const ground = leansDark ? sorted[0] : sorted[sorted.length - 1];
  const rest = sorted.filter((colour) => colour !== ground);
  return { ground, fields: leansDark ? rest.reverse() : rest };
}

/**
 * The two colours a gradient takes.
 *
 * Not the two ends, which is the obvious choice and the wrong one: the ends of
 * a palette are usually its neutrals, so a gradient drawn from them throws away
 * the colour the palette is for — a mint palette rendered white to black. It
 * leads with the liveliest colour instead, against whatever is furthest from
 * it in lightness. A palette with no lively colour has nothing to lead with and
 * falls back to its ends, earning its interest from contrast.
 */
export function paletteGradient(palette: Palette): { from: string; to: string } {
  const sorted = [...palette.colours].sort((a, b) => paletteLuminance(a) - paletteLuminance(b));
  const lead = liveliest(palette.colours);
  if (paletteChroma(lead) < 0.2) return { from: sorted[sorted.length - 1], to: sorted[0] };

  const partner = [...palette.colours].sort(
    (a, b) =>
      Math.abs(paletteLuminance(b) - paletteLuminance(lead)) - Math.abs(paletteLuminance(a) - paletteLuminance(lead)),
  )[0];
  return { from: lead, to: partner };
}

/** One colour, for a solid backdrop. */
export function paletteSolid(palette: Palette): string {
  return paletteRoles(palette).ground;
}

/** The pair a pattern draws with: its ground, and a shape colour that reads on it. */
export function palettePattern(palette: Palette): { from: string; to: string } {
  const { ground, fields } = paletteRoles(palette);
  return { from: fields[Math.min(1, fields.length - 1)], to: ground };
}

export function findPalette(id: number): Palette | undefined {
  return palettes.find((palette) => palette.id === id);
}

/**
 * Where a mesh puts its colour, as fractions of the frame.
 *
 * A preset rather than free positioning, the way the frame's shadow is three
 * drawn depths rather than a CSS box-shadow field: there is nothing here to
 * get wrong, and nothing to tune per project.
 *
 * `slot` indexes the palette's fields. `reach` is how far the blob fades out,
 * as a percentage — the fade is what makes it a mesh rather than a spotlight.
 */
export interface MeshBlob {
  x: number;
  y: number;
  slot: number;
  reach: number;
}

export const meshLayouts = ["drift", "bloom", "aurora", "pool", "corners", "halo"] as const;
export type MeshLayout = (typeof meshLayouts)[number];

export const meshLayoutLabels: Record<MeshLayout, string> = {
  drift: "Drift",
  bloom: "Bloom",
  aurora: "Aurora",
  pool: "Pool",
  corners: "Corners",
  halo: "Halo",
};

const MESH_BLOBS: Record<MeshLayout, readonly MeshBlob[]> = {
  drift: [
    { x: 18, y: 22, slot: 0, reach: 52 },
    { x: 82, y: 18, slot: 2, reach: 48 },
    { x: 72, y: 78, slot: 1, reach: 50 },
    { x: 12, y: 82, slot: 2, reach: 44 },
  ],
  bloom: [
    { x: 50, y: 6, slot: 0, reach: 62 },
    { x: 88, y: 48, slot: 2, reach: 46 },
    { x: 12, y: 62, slot: 1, reach: 42 },
  ],
  aurora: [
    { x: 28, y: 0, slot: 0, reach: 58 },
    { x: 72, y: 8, slot: 2, reach: 52 },
    { x: 50, y: 100, slot: 1, reach: 44 },
  ],
  pool: [
    { x: 20, y: 96, slot: 0, reach: 56 },
    { x: 78, y: 90, slot: 2, reach: 50 },
    { x: 50, y: 52, slot: 1, reach: 34 },
  ],
  corners: [
    { x: 0, y: 0, slot: 0, reach: 46 },
    { x: 100, y: 0, slot: 2, reach: 42 },
    { x: 0, y: 100, slot: 1, reach: 42 },
    { x: 100, y: 100, slot: 0, reach: 46 },
  ],
  halo: [
    { x: 50, y: 44, slot: 0, reach: 44 },
    { x: 16, y: 22, slot: 2, reach: 36 },
    { x: 86, y: 78, slot: 1, reach: 40 },
  ],
};

/**
 * A colour at zero alpha, which is not the same as `transparent`.
 *
 * `transparent` is transparent *black*, so a blob that fades to it drags a
 * grey halo out behind it — the exact thing that makes a hand-rolled mesh look
 * muddy rather than soft. Two lines, and invisible until they are missing.
 */
function faded(colour: string): string {
  const value = parseInt(colour.slice(1), 16);
  return `rgba(${(value >> 16) & 255},${(value >> 8) & 255},${value & 255},0)`;
}

/**
 * One arrangement as CSS gradients, outermost blob first.
 *
 * Wrapped on the field count: an arrangement asks for three and a three-colour
 * palette has two, which is a colour used twice rather than a hole in the card.
 */
export function meshLayers(layout: MeshLayout, fields: readonly string[]): string[] {
  return meshFields(layout, fields).map(
    (blob) => `radial-gradient(at ${blob.x}% ${blob.y}%, ${blob.colour} 0px, ${faded(blob.colour)} ${blob.reach}%)`,
  );
}

/**
 * The same arrangement, as the numbers rather than as CSS.
 *
 * The renderer draws a mesh in SVG — CSS radial gradients survive the editor
 * and the server but are dropped by the browser's exporter — and an SVG needs
 * the positions, not a string it would have to parse back out.
 *
 * Both forms come from one table, so the picker's CSS stand-in and the drawn
 * background cannot drift apart.
 */
export function meshFields(
  layout: MeshLayout,
  fields: readonly string[],
): { x: number; y: number; reach: number; colour: string }[] {
  return MESH_BLOBS[layout].map((blob) => ({
    x: blob.x,
    y: blob.y,
    reach: blob.reach,
    colour: fields[blob.slot % fields.length],
  }));
}
